import { SocialLoginMethod } from '@mopla/constants';

/**
 * Development environments where debug utilities should be enabled
 * All environments except production
 */
export const DEV_ENVIRONMENTS = [
	'dev-ds',
	'dev-rh',
	'dev',
	'development',
	'local',
	'pullrequest',
	'staging',
] as const;

export type DevEnvironment = typeof DEV_ENVIRONMENTS[number];

/**
 * Check if the current environment should have debug utilities enabled
 * @param currentEnv - The current environment string
 * @returns true if debug utilities should be enabled
 */
export const shouldEnableDevUtils = (currentEnv: string): boolean => {
	return (DEV_ENVIRONMENTS as readonly string[]).includes(currentEnv);
};

interface IFirebaseConfig {
	readonly apiKey: string;
	readonly authDomain: string;
	readonly projectId: string;
	readonly storageBucket: string;
	readonly messagingSenderId: string;
	readonly appId: string;
	readonly measurementId?: string;
}

/** Shared by driver and passenger apps */
export interface IMobileBaseEnvironment {
	readonly production: boolean;
	readonly baseUrl: string;
	readonly sentryDns: string;
	readonly env: string;
	readonly basename: string;
	readonly showLiveUpdatesVersion: boolean;
	readonly firebaseConfig: IFirebaseConfig;
	readonly allowedSocialLoginMethods: readonly SocialLoginMethod[];
}

interface IDeutschlandTicket {
	readonly priceChangeDate: string;
	readonly oldPrice: number;
	readonly newPrice: number;
	readonly messageVisibleFrom: string;
	readonly messageVisibleUntil: string;
}

interface IGoogleTagManager {
	readonly googleTagManagerContainerId: string;
}

export interface IFeatureFlags {
	readonly PRODTVouchersFeatureFlag: boolean;
	readonly isNewActivitiesEnabled?: boolean;
	/** Distributors whose rides make the On-Demand tab active in trip search. */
	readonly priorityDistributorIds: readonly string[];
}

/** Passenger-specific: extends base with payment, open banking, and feature flags */
export interface IPassengerEnvironment extends IMobileBaseEnvironment {
	readonly ticketshopBaseUrl: string;
	readonly paymentBaseUrl: string;
	readonly stripePublicKey: string;
	readonly stripeCustomCloseCallback: string;
	readonly stripeWebCallback: string;
	readonly stripeNativeCallback: string;
	readonly spenditValidationSetSource: string;
	readonly recaptchaSiteKey: string;
	readonly trueLayerRedirectUrl?: string;
	readonly trueLayerDeepLink?: string;
	readonly trueLayerAuthLinkWeb?: string;
	readonly trueLayerAuthLinkNative?: string;
	readonly enableOpenBanking: boolean;
	readonly deutschlandTicket: IDeutschlandTicket;
	readonly featureFlags: IFeatureFlags;
	readonly googleTagManager: IGoogleTagManager;
}

/** Payment app: standalone, no social login or Firebase */
export interface IPaymentEnvironment {
	readonly production: boolean;
	readonly env: string;
	readonly basename: string;
	readonly sentryDns: string;
	readonly stripePublicKey: string;
	readonly stripeRedirectUrl: string;
	readonly trueLayerRedirectUrl: string;
	readonly trueLayerAuthLink: string;
	readonly enableOpenBanking: boolean;
}
